"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { Box as MantineBox } from "@mantine/core";
import Link from "@/components/Link";

import type { HomepageBelowFoldContentProps } from "./Content";

const FALLBACK_DELAY_MS = 15_000;
const OBSERVER_ROOT_MARGIN = "0px 0px -35% 0px";
const ACTIVATION_EVENTS = ["pointerdown", "keydown"] as const;

const Content = dynamic<HomepageBelowFoldContentProps>(() => import("./Content"), {
  ssr: false,
});

type HomepageBelowFoldBoundaryProps = HomepageBelowFoldContentProps & {
  className?: string;
};

const HomepageBelowFoldBoundary = ({
  className,
  ...contentProps
}: HomepageBelowFoldBoundaryProps) => {
  const [activated, setActivated] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let activationComplete = false;
    let observer: IntersectionObserver | undefined;

    const cleanup = () => {
      window.clearTimeout(fallbackTimer);
      observer?.disconnect();
      ACTIVATION_EVENTS.forEach((event) => window.removeEventListener(event, activate));
    };

    const activate = () => {
      if (activationComplete) return;

      activationComplete = true;
      cleanup();
      setActivated(true);
    };

    const fallbackTimer = window.setTimeout(activate, FALLBACK_DELAY_MS);

    if (typeof IntersectionObserver !== "undefined" && rootRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) activate();
        },
        { rootMargin: OBSERVER_ROOT_MARGIN }
      );
      observer.observe(rootRef.current);
    }

    ACTIVATION_EVENTS.forEach((event) =>
      window.addEventListener(event, activate, { passive: true })
    );

    return cleanup;
  }, []);

  return (
    <MantineBox
      ref={rootRef}
      className={className}
      data-testid="homepage-below-fold-boundary"
      style={{ minHeight: "100dvh" }}
    >
      <noscript>
        <nav aria-label="Explore KURB">
          <Link href="/search">Search</Link>
          <Link href="/spotlight">Spotlight</Link>
          <Link href="/plans">Plans</Link>
          <Link href="/brands">Brands</Link>
          <Link href="/vendors">Vendors</Link>
          <Link href="/blog">Blog</Link>
        </nav>
      </noscript>
      {activated && <Content {...contentProps} />}
    </MantineBox>
  );
};

export default HomepageBelowFoldBoundary;
